import React from 'react';
import '../pages/Interview.css';
import { Camera, Mic, CheckCircle, AlertTriangle, VideoOff } from './InterviewIcons';
import DeviceSelect from './ui/DeviceSelect';
import MediaPlaceholder from './MediaPlaceholder';

export const DEVICE_CHECK_LABELS = Object.freeze({
    cameraPreview: 'Camera Preview',
    previewCaption: 'Your camera preview appears here',
    retest: 'Test Devices Again',
    working: 'Camera and microphone are working properly',
    test: 'Test Camera & Microphone',
    cameraLabel: 'Camera:',
    micLabel: 'Microphone:',
    selectPlaceholder: 'Available after the device test',
    cameraOption: (index) => `Camera ${index}`,
    micOption: (index) => `Microphone ${index}`,
    levelHeading: 'Microphone Level',
    noteReady: 'You can adjust your position and lighting before continuing. If the video appears blank, click on it to start the camera feed.',
    noteIdle: 'Run the device test to activate your camera preview and choose which camera and microphone to use.',
});

/**
 * Camera/mic check block: the two-column layout (camera well + controls) is
 * visible from the start — a quiet placeholder holds the camera well and the
 * selectors render inert with placeholder text until the device test runs, so
 * the stage's geometry never jumps when the live preview swaps in. Shared by
 * the setup page and the post-interruption resume screen so the device-check
 * machinery in InterviewSession is never duplicated.
 * Its own copy comes from `labels` shallow-merged over DEVICE_CHECK_LABELS;
 * the mediaTestError text still belongs to the caller.
 */
const DeviceCheckPanel = ({
    videoRef,
    mediaTestComplete,
    mediaTestError,
    onTest,
    availableCameras,
    availableMicrophones,
    selectedCameraId,
    selectedMicrophoneId,
    onCameraChange,
    onMicrophoneChange,
    audioLevel,
    labels = null
}) => {
    const L = { ...DEVICE_CHECK_LABELS, ...(labels || {}) };

    return (
        <div className="device-check-panel">
            <div className="device-check-columns">
                <div className="device-check-stage">
                    <h4>{L.cameraPreview}</h4>
                    <div className={`setup-video-container${mediaTestComplete ? '' : ' is-placeholder'}`}>
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="setup-video"
                            style={{ display: mediaTestComplete ? undefined : 'none', cursor: 'pointer' }}
                            onLoadedMetadata={() => {
                                if (videoRef.current && videoRef.current.srcObject) {
                                    videoRef.current.play().catch(() => {});
                                }
                            }}
                            onCanPlay={() => {
                                if (videoRef.current && videoRef.current.paused && mediaTestComplete) {
                                    videoRef.current.play().catch(() => {});
                                }
                            }}
                            onClick={() => {
                                if (videoRef.current && videoRef.current.paused) {
                                    videoRef.current.play().catch(() => {});
                                }
                            }}
                        />
                        {!mediaTestComplete && (
                            <MediaPlaceholder
                                icon={<VideoOff size={26} />}
                                caption={L.previewCaption}
                            />
                        )}
                    </div>
                </div>

                <div className="device-check-controls">
                    {/* Fixed-height status slot: test button → success line swap in
                        place, so nothing below shifts when the preview goes live */}
                    <div className="device-check-status">
                        {mediaTestError ? (
                            <div className="media-error">
                                <p className="error-text"><AlertTriangle size={14} style={{ marginRight: 4 }} /> {mediaTestError}</p>
                                <button className="btn btn-secondary" onClick={onTest}>
                                    {L.retest}
                                </button>
                            </div>
                        ) : mediaTestComplete ? (
                            <div className="media-success">
                                <p><CheckCircle size={14} style={{ marginRight: 4 }} />{' '}{L.working}</p>
                            </div>
                        ) : (
                            <button className="btn btn-secondary" onClick={onTest}>
                                {L.test}
                            </button>
                        )}
                    </div>

                    <div className="device-selectors">
                        <div className={`device-selector${availableCameras.length === 0 ? ' is-waiting' : ''}`}>
                            <label id="camera-select-label" htmlFor="camera-select"><Camera size={14} style={{ marginRight: 4 }} />{' '}{L.cameraLabel}</label>
                            <DeviceSelect
                                id="camera-select"
                                labelId="camera-select-label"
                                value={selectedCameraId}
                                placeholder={L.selectPlaceholder}
                                options={availableCameras.map((cam, i) => ({
                                    value: cam.deviceId,
                                    label: cam.label || L.cameraOption(i + 1)
                                }))}
                                onChange={onCameraChange}
                            />
                        </div>
                        <div className={`device-selector${availableMicrophones.length === 0 ? ' is-waiting' : ''}`}>
                            <label id="mic-select-label" htmlFor="mic-select"><Mic size={14} style={{ marginRight: 4 }} />{' '}{L.micLabel}</label>
                            <DeviceSelect
                                id="mic-select"
                                labelId="mic-select-label"
                                value={selectedMicrophoneId}
                                placeholder={L.selectPlaceholder}
                                options={availableMicrophones.map((mic, i) => ({
                                    value: mic.deviceId,
                                    label: mic.label || L.micOption(i + 1)
                                }))}
                                onChange={onMicrophoneChange}
                            />
                        </div>
                    </div>

                    <div className="audio-level-container">
                        <h4><Mic size={14} style={{ marginRight: 4 }} />{' '}{L.levelHeading}</h4>
                        <div className="audio-level-bar-container">
                            <div
                                className={`audio-level-bar ${audioLevel > 70 ? 'level-high' : audioLevel > 30 ? 'level-mid' : 'level-low'}`}
                                style={{ width: `${audioLevel}%` }}
                            />
                        </div>
                    </div>

                    <p className="preview-note">
                        {mediaTestComplete ? L.noteReady : L.noteIdle}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DeviceCheckPanel;
